import type { MarketListing, MarketProperty, MarketSubjectSnapshot, MarketTransaction } from "../../domain/types.ts";
import { marketListingToCandidate } from "../marketListingCandidate.ts";
import type { MarketCandidate, MarketCandidateOptions, MarketDataProvider } from "../marketDataProvider.ts";
import type { PublicMarketDiscoveryReport } from "../publicMarketTypes.ts";
import { parseRealityRadarXlsx } from "./realityRadarParser.ts";
import { IndexedDbRealityRadarSnapshotStore, type RealityRadarSnapshotStore } from "./realityRadarSnapshotStore.ts";
import type { RealityRadarImportSnapshot, RealityRadarImportSummary } from "./types.ts";

export interface MarketFileImportProvider extends MarketDataProvider {
  importFile(file: File | Blob, fileName?: string): Promise<RealityRadarImportSummary>;
  listImportSnapshots(): Promise<RealityRadarImportSnapshot[]>;
}

export class RealityRadarXlsxProvider implements MarketFileImportProvider {
  readonly id = "reality-radar-xlsx";
  private activeSnapshot?: RealityRadarImportSnapshot;
  private report?: PublicMarketDiscoveryReport;
  private readonly store: RealityRadarSnapshotStore;

  constructor(store: RealityRadarSnapshotStore = new IndexedDbRealityRadarSnapshotStore()) {
    this.store = store;
  }

  get available() {
    return Boolean(this.activeSnapshot);
  }

  getDiscoveryReport() {
    return this.report;
  }

  async importFile(file: File | Blob, fileName = "reality-radar.xlsx") {
    const resolvedName = "name" in file && typeof file.name === "string" ? file.name : fileName;
    if (!resolvedName.toLocaleLowerCase("sk").endsWith(".xlsx")) {
      throw new Error("Vyber XLSX export z Reality Radar.");
    }
    const snapshot = await parseRealityRadarXlsx(file, resolvedName);
    await this.store.save(snapshot);
    this.activeSnapshot = snapshot;
    this.report = {
      capturedAt: snapshot.capturedAt,
      rawCount: snapshot.rowsLoaded,
      normalizedCount: snapshot.normalizedRows,
      uniqueCount: snapshot.records.length,
      preliminaryCount: snapshot.records.length,
      detailCount: snapshot.records.length,
      requestCount: 0,
      sourceStatuses: [{
        providerId: this.id,
        sourceName: "Reality Radar XLSX",
        status: "ready",
        rawCount: snapshot.rowsLoaded,
        preliminaryCount: snapshot.records.length,
        detailCount: snapshot.records.length,
        requestCount: 0,
        message: `${snapshot.fileName} · nemenný snapshot`,
        capturedAt: snapshot.capturedAt
      }]
    };
    return {
      snapshotId: snapshot.id,
      capturedAt: snapshot.capturedAt,
      fileName: snapshot.fileName,
      rowsLoaded: snapshot.rowsLoaded,
      invalidRows: snapshot.invalidRows,
      invalidReasonCounts: snapshot.invalidReasonCounts ?? {},
      invalidPrimaryReasonCounts: snapshot.invalidPrimaryReasonCounts ?? {},
      invalidSamplesByReason: snapshot.invalidSamplesByReason ?? {},
      duplicateRowsRemoved: snapshot.duplicateRowsRemoved,
      duplicatesMerged: snapshot.duplicatesMerged,
      normalizedListings: snapshot.normalizedRows,
      uniqueListings: snapshot.records.length
    };
  }

  async findCandidates(subject: MarketSubjectSnapshot, _options?: MarketCandidateOptions): Promise<MarketCandidate[]> {
    if (!this.activeSnapshot) return [];
    return this.activeSnapshot.records.map((record) => ({
      ...marketListingToCandidate(record.listing, subject, this.id, {
        realityRadar: {
          connectorId: this.activeSnapshot?.connectorId,
          snapshotId: this.activeSnapshot?.id,
          fileName: this.activeSnapshot?.fileName,
          fileHash: this.activeSnapshot?.fileHash,
          capturedAt: this.activeSnapshot?.capturedAt,
          rawRows: record.rawRows
        }
      }),
      transactionType: record.transactionType as MarketSubjectSnapshot["transactionType"],
      municipalityId: record.municipalityId,
      citySource: record.municipalityId ? "dataset" : "custom"
    }));
  }

  async getListing(id: string): Promise<MarketListing | undefined> {
    return this.activeSnapshot?.records.find((record) => record.listing.id === id)?.listing;
  }

  async getHistoricalListings(propertyId: string) {
    const snapshots = await this.store.list();
    return snapshots.flatMap((snapshot) => snapshot.records)
      .filter((record) => record.listing.propertyId === propertyId)
      .map((record) => record.listing);
  }

  async getRealizedTransactions(): Promise<MarketTransaction[]> {
    return [];
  }

  async getProperty(): Promise<MarketProperty | undefined> {
    return undefined;
  }

  async listImportSnapshots() {
    return this.store.list();
  }
}

export const realityRadarXlsxProvider = new RealityRadarXlsxProvider();
